// Бургер-меню приложения: выдвижная панель, разбитая на секции.
//
// Раньше это было меню только мобильной полосы, куда сваливались все контролы
// подряд. Теперь у панели есть структура: «Поездка», «Персонализация»,
// «Аккаунт» — человек ищет глазами раздел, а не читает список из десяти строк.
// Секции задаёт вызывающий экран: у планировщика они одни, у списка поездок —
// другие, а сама панель ничего не знает ни про стор, ни про аккаунт.
//
// Контролы не дублируются: экран рендерит их один раз и кладёт сюда детьми,
// поэтому у них та же разметка, те же id и то же состояние, что снаружи.
import { useCallback, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useT } from '../../i18n';
import { useDismissablePanel } from './useDismissablePanel';
import styles from './AppMenu.module.css';

function BurgerIcon(): ReactElement {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

export interface AppMenuSection {
  /** Ключ списка и якорь для aria-подписи раздела. */
  id: string;
  title: string;
  children: ReactNode;
}

export interface AppMenuProps {
  /** Разделы панели сверху вниз. Пустые (children === null) не рисуются. */
  sections: AppMenuSection[];
}

/**
 * Строка раздела: подпись слева, контрол справа. `className` нужен экранам,
 * которые доразмеряют свои контролы под палец — сама строка про раскладку.
 */
export function AppMenuRow({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className={`${styles.row} ${className ?? ''}`}>
      <span className={styles.rowLabel}>{label}</span>
      {children}
    </div>
  );
}

/** Строка без подписи: кнопка или блок во всю ширину раздела. */
export function AppMenuBlock({ children }: { children: ReactNode }): ReactElement {
  return <div className={styles.block}>{children}</div>;
}

export function AppMenu({ sections }: AppMenuProps): ReactElement {
  const t = useT();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Esc и фокус-менеджмент — общая механика, см. useDismissablePanel.
  const sheetRef = useDismissablePanel(open, close);

  const visible = sections.filter((s) => s.children !== null && s.children !== false);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.burger}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('app.menu')}
        title={t('app.menu')}
        onClick={() => setOpen(true)}
      >
        <BurgerIcon />
      </button>

      {open && (
        <div
          className={styles.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div
            ref={sheetRef}
            className={styles.sheet}
            role="dialog"
            aria-modal="true"
            aria-label={t('app.menu')}
            tabIndex={-1}
          >
            <div className={styles.head}>
              <span className={styles.title}>{t('app.menu')}</span>
              <button
                type="button"
                className={styles.closeBtn}
                aria-label={t('form.close')}
                onClick={close}
              >
                ✕
              </button>
            </div>

            {/* Панель закрывается за тем, что уводит с неё: переход на другой
                экран или окно поверх. Помечает такие элементы сам экран
                атрибутом data-menu-close — контролы, которые крутят настройку
                на месте (кегль, валюта), остаются с открытым меню. */}
            <div
              className={styles.body}
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('[data-menu-close]')) setOpen(false);
              }}
            >
              {visible.map((section) => (
                <section key={section.id} className={styles.section}>
                  <h3 className={styles.legend}>{section.title}</h3>
                  <div className={styles.rows}>{section.children}</div>
                </section>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
